import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import billService from '@api/v1/billService'
import DataGrid from '@components/common/DataGrid'
import ControlCheckbox from '@components/common/ControlCheckbox'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'

const PAGE_SIZE = 50

const formatCurrency = (value) => {
  if (value == null) return '$0.00'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
}

/*
 * Stands in for a summary figure this page has not actually read.
 *
 * CC-53132: the alternative is a placeholder 0 (or $0.00), and beside an unfinished
 * read that is not a small number — it is a wrong one, in a card whose whole job is
 * to be believed at a glance. The dash is hidden from assistive tech and paired with
 * a real word, because a lone em dash announces as nothing at all and a screen-reader
 * user would hear the card's label followed by silence.
 */
const NotRead = () => (
  <>
    {/* gray-500, not gray-400: this is the only thing a sighted reader sees in
        the card, so it owes SC 1.4.3 (#6b7280 is 4.83:1 on white; #9ca3af is
        2.54:1 and fails even the 3:1 large-text floor). */}
    <span aria-hidden="true" className="text-gray-500">—</span>
    <span className="sr-only">Not loaded</span>
  </>
)

const statusBadge = (status) => {
  const s = (status || '').toLowerCase()
  if (s === 'approved') return { label: 'Approved', className: 'text-emerald-700 bg-emerald-50' }
  if (s === 'rejected') return { label: 'Rejected', className: 'text-red-700 bg-red-50' }
  return { label: 'Pending', className: 'text-amber-700 bg-amber-50' }
}

const PreBills = () => {
  useDocumentTitle('Pre-Bill Approval')
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [clientFilter, setClientFilter] = useState('all')
  const [matterFilter, setMatterFilter] = useState('all')
  const [selectedRows, setSelectedRows] = useState(new Set())
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [userSearchText, setUserSearchText] = useState('')

  // Fetch current user's approval permissions.
  //
  // Deliberately NOT surfaced when it doesn't resolve: everything it feeds fails
  // CLOSED (the "approve for others" switcher stays hidden), so an unresolved read
  // takes a capability away rather than making a claim about anyone's data. What it
  // does do is leave the settings read below permanently `idle` — see there.
  const { data: approvalInfo } = useQuery({
    queryKey: ['approvalInfo'],
    queryFn: () => billService.getApprovalInfo('00000000-0000-0000-0000-000000000000'),
  })

  // Fetch user settings (pre-bill queue) — changes when selectedUserId changes
  const settingsQuery = useQuery({
    queryKey: ['userSettings', selectedUserId || approvalInfo?.userId],
    queryFn: () => billService.getUserSettings(
      selectedUserId || approvalInfo?.userId,
      { outsideApproval: approvalInfo?.outsideApprove === 'True' }
    ),
    enabled: !!(selectedUserId || approvalInfo?.userId),
  })
  const { data: userSettings } = settingsQuery

  /*
   * CC-53132: this read is what makes the list a QUEUE rather than a list.
   *
   * `invoiceList` is the set of invoices assigned to this approver; when it is
   * missing the memo below falls back to every pre-bill the API returned. That
   * fallback is correct for "this user has no assignment list" and wrong for "we
   * never read the assignment list" — the two are indistinguishable from the data
   * alone, and the second one silently WIDENS the page while the heading goes on
   * calling it "your queue". Scope-widening on an unresolved read is the same
   * defect as a wrong number here, so the wording is gated on `queueScopeKnown`
   * and the two unresolved-for-a-reason states get their own surface below.
   *
   * `idle` is a genuine state for this one — the query is conditionally enabled,
   * so it is idle until `approvalInfo` yields a user id, and stays idle forever if
   * that read never lands. Per CC-53132's rule for a conditionally-enabled query,
   * idle is NOT an error and gets no alert: nobody asked yet. It is still not a
   * known scope, which is why `queueScopeKnown` demands 'ready' and nothing less.
   */
  const settingsState = useReadState('pre-bill-user-settings', settingsQuery)
  const queueScopeKnown = settingsState === 'ready'

  const preBillsQuery = useQuery({
    queryKey: ['preBills', statusFilter],
    queryFn: () => billService.getPreBills({
      status: statusFilter !== 'all' ? statusFilter : undefined,
      limit: 1000,
      offset: 0,
    }),
  })
  const { data, error, refetch } = preBillsQuery

  /*
   * CC-53132: three outcomes, not two — see `@api/v1/readState`.
   *
   * This page used to read `isLoading` and coerce the response with
   * `data?.bills || []`. `isLoading` is `isPending && isFetching`, so it is FALSE
   * for a query that is pending but NOT in flight — above all a PAUSED one
   * (react-query's default `networkMode: 'online'` while `onlineManager` believes
   * the browser is offline). `queryFn` is never called, nothing throws, nothing is
   * logged, and `data` stays `undefined`. Collapsed to `[]`, that rendered a
   * finished-looking page reading "0 pre-bills in your queue", "Total Value
   * $0.00", "Pending Review 0" and "No pre-bills found" — four separate factual
   * claims about a firm's unbilled work, made without ever having asked. There is
   * no request in any log on either side to contradict it, which is exactly why
   * this class survives server-side investigation.
   *
   * In a billing application "nothing is waiting for your approval" is not a
   * cosmetic error: it is the sentence that makes someone close the page.
   *
   * The hook rather than the bare `readState()` because it carries the permanent
   * blocked-read diagnostic (CC-49899 rule: a fail-soft path you cannot observe is
   * indistinguishable from a fix).
   */
  const preBillsState = useReadState('pre-bills', preBillsQuery)
  const preBillsReady = preBillsState === 'ready'

  /*
   * `undefined` has to survive every hop from here to the grid. `DataGrid` tells an
   * unresolved read from an empty one by `Array.isArray(data)` and swaps its
   * "No pre-bills found" line for an honest `role="alert"` one (and drops its row
   * count) when the read never landed — so `|| []` here is the defect itself, not a
   * tidy-up. It also covers a response that carries no `bills` key at all: something
   * came back, but not a list, and "not known to be empty" is the only truthful
   * reading of that.
   */
  const allBills = data?.bills

  // Filter to only bills in the user's approval queue (matching CenterbaseWeb behavior)
  // userSettings.invoiceList contains the invoice GUIDs assigned to this user
  const bills = useMemo(() => {
    // Nothing read, nothing to narrow. Returning `[]` here would manufacture an
    // answer out of the absence of one, and every count below is derived from this.
    if (!allBills) return undefined
    const assignedIds = userSettings?.invoiceList
    if (!assignedIds || assignedIds.length === 0) return allBills
    const idSet = new Set(assignedIds)
    const filtered = allBills.filter((b) => idSet.has(b.id))
    // If no matches (data mismatch), fall back to all bills
    return filtered.length > 0 ? filtered : allBills
  }, [allBills, userSettings])

  const total = bills?.length

  /*
   * The single predicate in front of every number on this page. `preBillsReady`
   * alone is not enough: a 200 whose body carries no `bills` array is a completed
   * read that still counted nothing, and `undefined.length` would print as
   * "undefined pre-bills" rather than refusing.
   */
  const countsKnown = preBillsReady && Array.isArray(bills)

  // Extract unique clients and matters for filter dropdowns
  const { clients, matters } = useMemo(() => {
    const clientSet = new Map()
    const matterSet = new Map()
    // Coerced, because a dropdown with no options states nothing — it offers
    // nothing. The claims are the counts and the empty state, and they are gated.
    ;(bills || []).forEach((b) => {
      if (b.billToContact) clientSet.set(b.billToContact, b.billToContact)
      if (b.matter) matterSet.set(b.matter, b.matter)
    })
    return {
      clients: [...clientSet.values()].sort(),
      matters: [...matterSet.values()].sort(),
    }
  }, [bills])

  // Client-side search + filter
  const filteredBills = useMemo(() => {
    // Straight through: the grid is the component that can tell `undefined` from
    // `[]`, and a filter over nothing is not an empty result.
    if (!bills) return undefined
    let result = bills
    if (searchTerm.length >= 2) {
      const term = searchTerm.toLowerCase()
      result = result.filter((b) =>
        (b.invoiceNumber || '').toLowerCase().includes(term) ||
        (b.matter || '').toLowerCase().includes(term) ||
        (b.billToContact || '').toLowerCase().includes(term) ||
        (b.attorney || '').toLowerCase().includes(term)
      )
    }
    if (clientFilter !== 'all') {
      result = result.filter((b) => b.billToContact === clientFilter)
    }
    if (matterFilter !== 'all') {
      result = result.filter((b) => b.matter === matterFilter)
    }
    return result
  }, [bills, searchTerm, clientFilter, matterFilter])

  // Summary calculations. Safe to compute over nothing — `countsKnown` decides
  // whether the result may be SHOWN, which is the half that was wrong. A $0.00
  // computed from an unread list is the most quietly wrong figure on the page.
  const summary = useMemo(() => {
    const src = bills || []
    const totalValue = src.reduce((sum, b) => sum + (b.invoiceTotal || 0), 0)
    const pendingCount = src.filter((b) => {
      const s = (b.approvalStatus || '').toLowerCase()
      return !s || s === 'pending'
    }).length
    return { totalValue, pendingCount }
  }, [bills])

  const canApproveForOthers = approvalInfo?.canApprove === true
  // No `?? 0`: this is a count, and the render site below gates it on a completed
  // read rather than letting an unread queue arrive as a plausible zero.
  const upcomingCount = userSettings?.invoiceList?.length

  // Whose queue the page is describing — the admin switcher can point it elsewhere.
  const whose = selectedUserId ? 'their' : 'your'

  /*
   * The subtitle is this page's headline claim and it rests on TWO reads: the
   * pre-bill list, and the assignment list that narrows it into a queue. It is
   * split rather than simply hidden because every state has to render SOMETHING —
   * a heading that disappears mid-load reads as a rendering fault, and the first
   * cut of this fix on the catalog page left an unhandled state rendering a blank.
   *
   *   both reads landed        → the original sentence, untouched
   *   list landed, scope did not → the count is real; the word "queue" would not be
   *   list did not land          → no number at all, and say which case it is
   *
   * 'idle' rides with 'loading' for the pre-bills query only: that query carries no
   * `enabled:` option so it cannot reach idle today, but naming it here means
   * nobody adding one later gets a silently mis-worded heading. The settings query
   * IS conditionally enabled and its idle is handled separately — see above.
   */
  const subtitle = countsKnown
    ? (queueScopeKnown
      ? `${total} pre-bill${total !== 1 ? 's' : ''} in ${whose} queue`
      : `${total} pre-bill${total !== 1 ? 's' : ''} — not narrowed to ${whose} approval queue`)
    : (preBillsState === 'loading' || preBillsState === 'idle')
      ? `Loading ${whose} queue…`
      : `${selectedUserId ? 'Their' : 'Your'} queue has not been loaded`

  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
  }

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value)
    setSelectedRows(new Set())
  }

  const toggleRow = useCallback((id) => {
    setSelectedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    // `filteredBills` is undefined until the read lands; selecting "all of nothing"
    // is a no-op either way, but reading `.length` off it would throw.
    const selectable = filteredBills || []
    setSelectedRows((prev) => {
      if (prev.size === selectable.length) return new Set()
      return new Set(selectable.map((b) => b.id))
    })
  }, [filteredBills])

  const handleBatchApprove = async () => {
    const ids = Array.from(selectedRows)
    await Promise.all(ids.map((id) => billService.approveBill(id, { action: 'approve' })))
    setSelectedRows(new Set())
    refetch()
  }

  const handleBatchPost = async () => {
    const ids = Array.from(selectedRows)
    await billService.batchPostBills(ids)
    setSelectedRows(new Set())
    refetch()
  }

  const handleRowClick = (row) => {
    const billId = row.id || row.objectId
    if (billId) navigate(`/pre-bills/${billId}`)
  }

  const selectableCount = filteredBills?.length ?? 0

  const columns = useMemo(() => [
    {
      id: 'select',
      /*
       * spec 121 gap 2 (2026-09-09) — the last two inert-utility native checkboxes
       * in the tree.
       *
       * They carried `rounded border-gray-300 text-cb-accent-text
       * focus:ring-cb-accent/30`, and measured by compiling this repo's own
       * src/index.css with its tailwindcss@4.2.1, THREE of those four emit nothing
       * that paints: `border-gray-300` emits only `border-color` against preflight's
       * `border-width: 0`; `focus:ring-cb-accent/30` emits only `--tw-ring-color`
       * with no ring width; `text-cb-accent-text` emits only `color`, which does not
       * reach a native checkbox's box or tick (`accent-color` is the only property
       * that does). Only `rounded` did anything. So the browser drew its own control
       * and a token swap here would have been zero pixels.
       *
       * This is NOT the same defect as ARReport's: nothing drawn sits beside these,
       * so there is no visible same-popover mismatch, and contrast does not move
       * either way (Chrome's UA #767676 is 4.542:1 on white and passes; rc-seam
       * #6b7280 is 4.834:1 and passes). It is consistency work — and PreBills is the
       * only DataGrid list page whose row-selection boxes were native while Contacts
       * and Matters got the drawn one, because this page rolls its own `select`
       * column rather than passing `enableSelection`. One click away in its own
       * Columns popover the drawn box was already on screen.
       *
       * `hit24` is REQUIRED on both: they are named by `aria-label` with no wrapping
       * <label>, so there is no larger target to inherit and the component's default
       * 14x14 input would be under SC 2.5.8's 24px floor.
       * `onClick`'s stopPropagation MUST survive or a selection click opens the
       * pre-bill; it reaches the input through the component's rest spread.
       * PreBills.test.jsx's five checkbox tests are all wrapped in
       * `if (checkboxes.length > 1)`, so they would have passed even if this
       * conversion had deleted the inputs entirely. The real positive assertion —
       * a painted border, `border-rc-seam`, the 24x24 overlay, and negative twins
       * for all three inert utilities — is the "spec 121 gap 2" case in that same
       * file, outside any such guard.
       */
      header: () => (
        <ControlCheckbox
          hit24
          aria-label="Select all pre-bills"
          checked={selectableCount > 0 && selectedRows.size === selectableCount}
          onChange={toggleAll}
        />
      ),
      cell: ({ row }) => (
        <ControlCheckbox
          hit24
          aria-label={`Select pre-bill ${row.original.invoiceNumber || row.index + 1}`}
          checked={selectedRows.has(row.original.id)}
          onChange={(e) => { e.stopPropagation(); toggleRow(row.original.id) }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      size: 40,
      enableSorting: false,
    },
    {
      id: 'invoiceNumber',
      header: 'Invoice #',
      accessorKey: 'invoiceNumber',
      cell: ({ getValue }) => <span className="font-bold text-cb-accent-text">{getValue() || 'Draft'}</span>,
      size: 120,
    },
    {
      id: 'matter',
      header: 'Matter',
      accessorKey: 'matter',
      size: 200,
    },
    {
      id: 'billToContact',
      header: 'Client',
      accessorKey: 'billToContact',
      size: 160,
    },
    {
      id: 'attorney',
      header: 'Attorney',
      accessorKey: 'attorney',
      size: 150,
    },
    {
      id: 'invoiceTotal',
      header: 'Total',
      accessorKey: 'invoiceTotal',
      cell: ({ getValue }) => <span className="font-medium">{formatCurrency(getValue())}</span>,
      size: 110,
    },
    {
      id: 'issueDate',
      header: 'Date',
      accessorKey: 'issueDate',
      cell: ({ getValue }) => {
        const v = getValue()
        return v ? dayjs(v).format('M/D/YYYY') : ''
      },
      size: 110,
    },
    {
      id: 'approvalStatus',
      header: 'Status',
      accessorKey: 'approvalStatus',
      cell: ({ getValue }) => {
        const badge = statusBadge(getValue())
        return (
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${badge.className}`}>
            {badge.label}
          </span>
        )
      },
      size: 100,
    },
  ], [selectableCount, selectedRows, toggleAll, toggleRow])

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display text-gray-900">Pre-Bill Approval</h1>
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        </div>
      </div>

      {/* User Switcher — admin "approve for others" */}
      {canApproveForOthers && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
            </svg>
            <span className="text-sm font-medium text-blue-800">Viewing as:</span>
          </div>
          <div className="flex-1 max-w-xs relative">
            {/* aria-label, not placeholder-only -- see CC-53430. */}
            <input
              type="text"
              placeholder="Search users to approve for..."
              aria-label="Search users to approve for"
              value={userSearchText}
              onChange={(e) => setUserSearchText(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-blue-200 rounded-lg text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
          {selectedUserId && (
            <button
              onClick={() => { setSelectedUserId(null); setUserSearchText('') }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Reset to me
            </button>
          )}
          {/* Gated on the settings read having COMPLETED, not merely on the number
              being non-zero. `undefined > 0` is already false, so this changes
              nothing today — it is here so the next edit cannot reintroduce a
              `?? 0` and turn an unread queue into "0 pre-bills in queue". */}
          {queueScopeKnown && upcomingCount > 0 && (
            <span className="text-sm text-blue-700 font-medium">
              {upcomingCount} pre-bill{upcomingCount !== 1 ? 's' : ''} in queue
            </span>
          )}
        </div>
      )}

      {/* Summary Cards.

          CC-53132: all three are figures, and a figure is a claim. A card is read at
          a glance and taken at face value — more readily than the grid, which at
          least shows its working — so an unresolved read prints `NotRead` rather
          than the 0 / $0.00 / 0 it used to. The labels stay put in every state so
          the layout does not jump, and the panel below the filters says why.

          A RESOLVED zero still renders as a zero: "this firm has nothing awaiting
          approval" is a real and useful answer, and suppressing it would be a
          different bug from the one being fixed. */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Total Pre-Bills</p>
          <p className="text-2xl font-semibold text-gray-900 mt-1">{countsKnown ? total : <NotRead />}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Total Value</p>
          <p className="text-2xl font-semibold text-gray-900 mt-1">{countsKnown ? formatCurrency(summary.totalValue) : <NotRead />}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Pending Review</p>
          <p className="text-2xl font-semibold text-amber-600 mt-1">{countsKnown ? summary.pendingCount : <NotRead />}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="flex-1 min-w-[200px] max-w-md relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            aria-label="Search pre-bills"
            placeholder="Search pre-bills..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent"
          />
        </div>
        <select
          aria-label="Filter by status"
          value={statusFilter}
          onChange={handleStatusChange}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent"
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select
          aria-label="Filter by client"
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent max-w-[200px]"
        >
          <option value="all">All Clients</option>
          {clients.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          aria-label="Filter by matter"
          value={matterFilter}
          onChange={(e) => setMatterFilter(e.target.value)}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent max-w-[200px]"
        >
          <option value="all">All Matters</option>
          {matters.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {/* The assignment read is the one that decides whether this is a QUEUE.
          Announced only when it was actually attempted and did not land — an `idle`
          settings query means nobody asked yet (it waits on `approvalInfo`), and
          shouting about a read that was never requested trains people to ignore the
          banner. Sits above the grid because it changes what the rows below MEAN,
          not whether they are there. */}
      {(settingsState === 'blocked' || settingsState === 'failed') && (
        <div
          role="alert"
          className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <p className="font-medium">
            We could not read {whose} approval queue, so this list is not narrowed to it.
          </p>
          <p className="mt-1 text-amber-800">
            These are all the pre-bills the server returned, not the ones assigned to{' '}
            {selectedUserId ? 'that approver' : 'you'} — treat the figures above as unscoped.
          </p>
          <button
            type="button"
            onClick={() => settingsQuery.refetch()}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Reload queue
          </button>
        </div>
      )}

      {/* Error state. The panel already stood in front of the grid, which is right —
          but it had `role` on nothing, said nothing about what the blank page meant,
          and offered no way back except reloading the app. `refetch` is free. */}
      {preBillsState === 'failed' ? (
        <div role="alert" className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <p className="text-sm text-red-500">Failed to load pre-bills: {error.message}</p>
          <p className="mt-1.5 text-sm text-gray-500">
            This is not an empty approval queue — nothing was read, so no pre-bill is
            missing and none has been approved or posted.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 rounded-lg border border-rc-control bg-white px-3 py-1.5 text-xs font-medium text-cb-accent-text hover:bg-gray-50"
          >
            Try again
          </button>
        </div>
      ) : preBillsState === 'blocked' ? (
        /* The state that used to render as "0 pre-bills in your queue" over
           "No pre-bills found". react-query PAUSED the fetch, so `queryFn` was never
           called: no request, no error, no spinner, nothing in any log on either
           side. A spinner forever would be nearly as bad — the user still concludes
           the app is broken rather than that their firm's unbilled work is unread —
           so say what actually happened and offer the same retry the failure does. */
        <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
          <p className="font-medium">
            Can&rsquo;t reach the server, so we haven&rsquo;t loaded {whose} pre-bills.
          </p>
          <p className="mt-1 text-amber-800">
            This is not an empty approval queue — the pre-bill list was never read, and
            no total on this page has been counted. Your connection looks offline;
            check it and try again.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      ) : (
        <DataGrid
          columns={columns}
          /* Deliberately the raw value: `DataGrid` is the component that can tell
             `undefined` from `[]`, and collapsing it here is what let a never-sent
             read print "No pre-bills found". */
          data={filteredBills}
          /* 'idle' folded into loading: this query has no `enabled:` option so it
             cannot reach idle today, but every branch here is gated on a NAMED
             state and an unhandled one would hand the grid `isLoading={false}` with
             `data === undefined`. If anyone adds an `enabled:` gate, revisit this
             line FIRST — idle then becomes the ordinary "not asked yet" and wants
             its own copy rather than a spinner that can never resolve. */
          isLoading={preBillsState === 'loading' || preBillsState === 'idle'}
          emptyMessage="No pre-bills found"
          /* Reached when the read completed but carried no `bills` array — the
             failed and blocked states are handled above and never get this far. */
          unresolvedMessage="We couldn't read your pre-bills, so this is not known to be an empty queue. Reload to try again."
          loadingMessage="Loading pre-bills..."
          onRowClick={handleRowClick}
          pageSize={PAGE_SIZE}
          enableExport={true}
          enableFiltering={true}
          exportFilename="pre-bills"
        />
      )}

      {/* Batch Action Bar */}
      {selectedRows.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-cb-navy text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-4 z-50">
          <span className="text-sm">{selectedRows.size} selected</span>
          <button onClick={handleBatchApprove} className="px-4 py-1.5 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600">
            Approve
          </button>
          <button onClick={handleBatchPost} className="px-4 py-1.5 bg-cb-accent text-white rounded-lg text-sm font-medium hover:bg-cb-accent/90">
            Post to Ledger
          </button>
          <button onClick={() => setSelectedRows(new Set())} className="text-sm text-white/70 hover:text-white">
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}

export default PreBills
